import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { StandField } from "@/pages/adjustments/manager/components/StandField";
import { useStandsByMillAdjustment } from "@/pages/adjustments/manager/components/StandsOfMillAdjustmentSelector";
import { MillAdjustmentByShift } from "@/pages/adjustments/manager/models/MillAdjustmentByShift";
import {
	setStandsToMillAdjustment,
	upsertMillAdjustment,
} from "@/pages/adjustments/manager/repositories/AdjustmentManagerRepository";
import { Stand } from "@/shared/models/Stand";
import { AdjustmentReasonSelector } from "@/shared/selectors/AdjustmentReasonSelector";
import { AdjustmentTypeSelector } from "@/shared/selectors/AdjustmentTypeSelector";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Box, Grid2, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { AdjustmentsTable } from "../table/AdjustmentsTable";
import { ToggleButtonSearch } from "./ToogleButtonSearch";

const moment = getMomentTz();

type Props = {
	assetId?: number | null;
	showModal: "new" | "select" | "";
	onHide: (update: boolean) => void;
	millAdjustmentId: number | null;
	delay?: { delayId: number; delayEnd: Date | null };
	suffixTitle?: string;
};

export const NewSelectAdjustmentModal = ({
	assetId,
	showModal,
	onHide,
	millAdjustmentId,
	delay,
	suffixTitle = "Delays Manager",
}: Props) => {
	const [selectedAdjustment, setSelectedAdjustment] =
		useState<MillAdjustmentByShift | null>(null);
	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);
	const [searchByExistings, setSearchByExistings] = useState(false);

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [title, setTitle] = useState("");

	const { getShiftDataFromAPI } = useShiftNavigator();

	const { data: d } = useStandsByMillAdjustment({
		millAdjustmentId: millAdjustmentId ?? 0,
		onError: (error) => setError(error.message),
		onSuccess: (data) => {
			if (data && data.length > 0) {
				setStandsSelected([...data]);
			}
		},
	});

	const { control, handleSubmit, reset, watch, setValue } =
		useForm<MillAdjustmentByShift>({
			defaultValues: {},
		});

	const timestamp = watch("Timestamp");
	const requiredValue = watch("RequiredValue");
	const requiredReason = watch("RequiredReason");

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertMillAdjustment, {
		meta: { successMessage: "The adjustment was saved successfully" },
		onSuccess: async (id: number) => {
			if (id && standsSelected) {
				const resp = await setStandsToMillAdjustment(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}
			if (!delay) {
				// Navigate to new adjustment
				const formattedDate = moment(timestamp);
				await getShiftDataFromAPI(formattedDate.toDate(), 0);
				await queryClient.invalidateQueries({ queryKey: ["adjustments"] });
			}

			onHide(true);
			setStandsSelected([]);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<MillAdjustmentByShift> = async (
		adjustment: MillAdjustmentByShift,
	) => {
		setIsSubmitLoading(true);
		if (searchByExistings) {
			upsertMutation.mutate({
				millAdjustmentId: selectedAdjustment!.MillAdjustmentId,
				timestamp: selectedAdjustment!.Timestamp!,
				adjustmentTypeId: selectedAdjustment!.AdjustmentTypeId!,
				adjustmentReasonId: selectedAdjustment!.AdjustmentReasonId!,
				operatorId: selectedAdjustment!.OperatorId!,
				adjustmentValue: selectedAdjustment!.AdjustmentValue,
				unit: selectedAdjustment!.Unit,
				delayId: delay?.delayId ?? null,
				comments: selectedAdjustment?.Comments ?? "",
			});
		} else {
			upsertMutation.mutate({
				millAdjustmentId: adjustment.MillAdjustmentId,
				timestamp: adjustment.Timestamp!,
				adjustmentTypeId: adjustment.AdjustmentTypeId!,
				adjustmentReasonId: adjustment.AdjustmentReasonId!,
				operatorId: adjustment.OperatorId!,
				adjustmentValue: adjustment.AdjustmentValue,
				unit: adjustment.Unit,
				delayId: adjustment.DelayId,
				comments: adjustment?.Comments ?? "",
			});
		}
	};

	useEffect(() => {
		if (showModal !== "") {
			setIsSubmitLoading(false);
			if (searchByExistings) {
				setTitle("SELECT MILL ADJUSTMENT");
			} else {
				setTitle(`NEW MILL ADJUSTMENT`);
			}
			if (showModal === "new") {
				reset({
					MillAdjustmentId: null,
					Timestamp: delay?.delayEnd || new Date(),
					AdjustmentTypeId: null,
					AdjustmentReasonId: null,
					OperatorId: null,
					AdjustmentValue: null,
					Unit: null,
					DelayId: delay?.delayId,
				});
			}
		} else {
			reset();
		}
	}, [showModal, delay, searchByExistings]);

	useEffect(() => {
		setSearchByExistings(!!(delay && millAdjustmentId));
	}, [delay, millAdjustmentId, showModal]);

	return (
		<>
			{showModal === "new" && (
				<HelmetDexteel
					title={`${
						!searchByExistings ? "New" : "Select"
					} Adjustment - ${suffixTitle}`}
				/>
			)}
			<MesfModal
				maxWidth="lg"
				open={showModal !== ""}
				handleClose={() => {
					onHide(false);
					setStandsSelected([]);
				}}
				id="upsertDelay"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						{delay && (
							<Grid2
								size={{ md: 12 }}
								style={{ marginBottom: 15 }}
								justifyContent="center"
							>
								<ToggleButtonSearch
									value={!searchByExistings ? "new" : "search"}
									onChange={() => setSearchByExistings(!searchByExistings)}
								/>
							</Grid2>
						)}
						{searchByExistings ? (
							<AdjustmentsTable
								key={`${millAdjustmentId}`}
								delayId={delay?.delayId!}
								millAdjustmentId={millAdjustmentId}
								onSelectionChange={setSelectedAdjustment}
							/>
						) : (
							<>
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Timestamp"
												control={control}
												rules={{
													required: "Timestamp date is required",
													validate: {
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "Timestamp date cannot be in the future.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														label="Timestamp"
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														timezone={TimeService.getInstance().getServerTimeZone()}
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														slotProps={{
															textField: {
																size: "small",
																error: !!error,
																helperText: error?.message,
																fullWidth: true,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Stands"
												control={control}
												render={() => (
													<StandField
														label="Stands"
														onSelectionChanged={setStandsSelected}
														standsOfMillAdjustment={standsSelected}
														millAdjustmentId={millAdjustmentId}
													/>
												)}
											/>
										</Box>
									</Grid2>

									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentTypeId"
												control={control}
												rules={{
													required: "Type is required",
												}}
												render={({ field, fieldState: { error } }) => (
													<AdjustmentTypeSelector
														label="Type"
														value={field.value}
														onChange={(
															id,
															requiredValue,
															requiredReason,
															unit,
														) => {
															setValue("AdjustmentTypeId", id);
															setValue("RequiredValue", requiredValue);
															setValue("AdjustmentValue", null);
															setValue("RequiredReason", requiredReason);
															setValue("AdjustmentReasonId", null);
															setValue("Unit", unit);
														}}
														fieldError={error}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentReasonId"
												control={control}
												rules={{
													required: requiredReason
														? "Reason is required"
														: false,
												}}
												render={({ field, fieldState: { error } }) => (
													<AdjustmentReasonSelector
														label="Reason"
														value={field.value}
														onChange={field.onChange}
														disabled={!requiredReason}
														fieldError={error}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="OperatorId"
												rules={{ required: "Operator is required" }}
												control={control}
												render={({ field, fieldState: { error } }) => (
													<OperatorSelector
														assetId={assetId ?? undefined}
														label="Operator"
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														required
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 3 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentValue"
												rules={{
													required: requiredValue
														? "The value is required"
														: false,
												}}
												control={control}
												render={({ field, fieldState: { error } }) => (
													<TextField
														label="Value"
														value={field.value ?? ""}
														onChange={(e) => {
															field.onChange(e.target.value);
														}}
														variant="outlined"
														fullWidth
														size="small"
														error={!!error}
														helperText={error?.message}
														type="number"
														disabled={!requiredValue}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 3 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Unit"
												control={control}
												render={({ field }) => (
													<TextField
														label="Unit"
														value={field.value ?? ""}
														variant="outlined"
														fullWidth
														size="small"
														disabled
													/>
												)}
											/>
										</Box>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
						<FooterModalButtons
							disabled={searchByExistings && !selectedAdjustment}
							isSubmitLoading={isSubmitLoading}
							onHide={(boolean) => {
								onHide(boolean);
								setStandsSelected([]);
							}}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
